import {
  isEmbeddedInIframe,
  notifyParentToLogin,
  requestAuthCacheFromParent,
} from './parentTokenRelay';

export class AuthSessionExpiredError extends Error {
  constructor() {
    super('Auth0 session expired');
    this.name = 'AuthSessionExpiredError';
  }
}

let _getAccessTokenSilently: (() => Promise<string>) | null = null;
let _loginWithRedirect: ((options: { appState: { returnTo: string } }) => Promise<void>) | null = null;

export const setTokenRefresher = (fn: () => Promise<string>) => {
  _getAccessTokenSilently = fn;
};

export const setLoginCallback = (
  fn: (options: { appState: { returnTo: string } }) => Promise<void>
) => {
  _loginWithRedirect = fn;
};

export const redirectToLogin = () => {
  // A top-level loginWithRedirect() can never complete inside this iframe —
  // Universal Login refuses to be framed, and silent auth needs a
  // third-party cookie unavailable in this nested context. Layout is a real
  // top-level browsing context, so it's the only place this redirect can
  // actually succeed; once it does, this iframe's own boot-time seeding
  // picks the fresh session back up automatically.
  if (isEmbeddedInIframe()) {
    notifyParentToLogin();
    return;
  }
  const returnTo = window.location.pathname + window.location.search;
  if (_loginWithRedirect) {
    _loginWithRedirect({ appState: { returnTo } });
  } else {
    window.location.assign(window.location.href);
  }
};

// Uses Web Locks to serialize token refresh across all tabs on the same origin.
// This prevents concurrent tabs from simultaneously using the same refresh token,
// which triggers Auth0's reuse detection and can invalidate the entire session.
// Exported so any getAccessTokenSilently() call site (not just getToken() below)
// can be coordinated across tabs. Do not wrap a function that is itself invoked
// through getToken()/acquireToken() elsewhere — nesting two acquireToken() calls
// on the same lock from the same single-threaded page will deadlock.
export const acquireToken = (fn: () => Promise<string>): Promise<string> => {
  // When embedded, a local failure isn't necessarily a dead session — IMAS
  // never holds a refresh token while framed (see IntegrityConnectIframe's
  // relay), so its own silent refresh runs out on a clock Layout doesn't
  // share. Ask Layout for a fresh one before giving up: on success, re-run
  // fn() so auth0-react re-derives isAuthenticated/user from the freshly
  // seeded cache rather than just handing back a bare string.
  const run = async (): Promise<string> => {
    try {
      return await fn();
    } catch (error) {
      if (isEmbeddedInIframe()) {
        let relayed: string | null = null;
        try {
          relayed = await requestAuthCacheFromParent();
        } catch (relayError) {
          console.error('[Auth0] parent token relay failed:', relayError);
        }
        if (relayed) {
          try {
            return await fn();
          } catch (retryError) {
            console.error('[Auth0] getAccessTokenSilently failed after parent token relay:', retryError);
            throw new AuthSessionExpiredError();
          }
        }
      }
      console.error('[Auth0] getAccessTokenSilently failed:', error);
      throw new AuthSessionExpiredError();
    }
  };

  if (typeof navigator !== 'undefined' && navigator.locks) {
    return new Promise<string>((resolve, reject) => {
      navigator.locks.request('auth0_token_refresh', async () => {
        try {
          resolve(await run());
        } catch (error) {
          reject(error);
        }
      });
    });
  }
  return run();
};

export const getToken = async (): Promise<string> => {
  if (!_getAccessTokenSilently) return '';
  return acquireToken(_getAccessTokenSilently);
};

class LoggedInUser {
  displayName: string = '';
  userName: string = '';
  initialsName: string = '';
  adUserId: string = '';
}
export default new LoggedInUser();